import { take, takeLatest, put, call, fork, cancel, cancelled } from 'redux-saga/effects';
import { getUserFromToken } from '../utilities/jwt';

import {
  REQUEST_LOGIN,
  AUTH_ERROR,
  REQUEST_LOGOUT,
  UPDATE_LOCALE_START,
  SKIP_LOGIN,
  RESET_PASSWORD_VALIDATE_TOKEN_REQUEST,
  RESET_PASSWORD_REQUEST,
  RESET_PASSWORD_RESET_PROCESS,
  REQUEST_PASSWORD_RESET_REQUEST,
  CHANGE_PASSWORD_REQUEST,
} from '../actions';
import getStoredLanguage from '../utilities/language';

angular.module('tct.sagas.auth', [])
  .factory('authSagaFactory', authSagaFactory);

authSagaFactory.$inject = ['$ngRedux',
  'authService',
  'actionsFactory',
  '$state',
  'localeService',
  '$translate',
  'mappingService',
  'notie',
];

const localStorage = window.localStorage || {};

function authSagaFactory($ngRedux,
  authService, actionsFactory, $state, localeService, $translate, mappingService, notie) {
  const factory = {
    loginFlow,
    localeFlow,
    resetPasswordFlow,
    requestResetPasswordFlow,
    changePasswordFlow,
  };

  return factory;

  function* requestResetPassword(action) {
    try {
      yield call(authService.requestResetPassword, action.email);
      yield put(actionsFactory.requestResetPasswordSuccess());
    } catch (error) {
      yield put(actionsFactory.requestResetPasswordFailed(error.data && error.data.error
        ? error.data.error.message
        : process.env.ERR_GENERIC));
    }
  }

  function* requestResetPasswordFlow() {
    yield takeLatest(REQUEST_PASSWORD_RESET_REQUEST, requestResetPassword);
  }

  function* validateToken(action) {
    try {
      const response = yield call(
        authService.validateResetPasswordToken,
        action.token,
        action.email);
      yield put(actionsFactory.validateResetPasswordTokenSuccess(
        mappingService.objectKeysToCamel(
          response.data && response.data.data)));
    } catch (error) {
      yield put(actionsFactory.validateResetPasswordTokenFailed(
        error.data && error.data.error ? error.data.error.message : process.env.ERR_GENERIC));
    }
  }

  function* resetPassword(action) {
    try {
      if (action.type === RESET_PASSWORD_REQUEST) {
        yield call(
          authService.resetPassword,
          action.token,
          action.email,
          action.password,
          action.passwordConfirmation);

        yield put(actionsFactory.resetPasswordSuccess());
      }
    } catch (error) {
      yield put(actionsFactory.resetPasswordFailed(
        error.data && error.data.errors && error.data.errors.password
          ? error.data.errors.password[0]
          : process.env.ERR_GENERIC));
    }
  }


  function* resetPasswordFlow() {
    yield takeLatest(RESET_PASSWORD_VALIDATE_TOKEN_REQUEST, validateToken);
    yield takeLatest([
      RESET_PASSWORD_REQUEST, RESET_PASSWORD_RESET_PROCESS,
    ], resetPassword);
  }

  function* changePassword(action) {
    const { currentPassword, password, passwordConfirmation } = action;
    try {
      yield call(authService.changePassword, currentPassword, password, passwordConfirmation);
      yield put(actionsFactory.changePasswordSuccess());
      $state.go('authenticated.home');
      $translate('CHANGE_PASSWORD_SUCCESS')
        .then((message) => {
          notie.alert({
            type: 'success',
            text: message,
            position: 'bottom',
            time: 10,
          });
        });
    } catch (error) {
      let message = error.data && error.data.error
        ? error.data.error.message
        : undefined;

      if (!message) {
        message = error.data && error.data.errors && error.data.errors.password
          ? error.data.errors.password[0]
          : process.env.ERR_GENERIC;
      }

      yield put(actionsFactory.changePasswordFailed(message));
    }
  }

  function* changePasswordFlow() {
    yield takeLatest(CHANGE_PASSWORD_REQUEST, changePassword);
  }

  function* authorize(username, password) {
    try {
      const authRes = yield call(authService.login, username, password);
      const jwtToken = authRes.data && authRes.data.data && authRes.data.data.access_token;
      const user = getUserFromToken(jwtToken);

      yield put(actionsFactory.authGranted(jwtToken, user));
      localStorage.setItem('jwt_token', jwtToken);
      let home = null;
      let params = {};
      switch (user.type) {
        case 'editor':
        case 'master':
        case 'director':
        case 'buyer':
          home = 'authenticated.home';
          break;
        default:
          home = 'authenticated.company.detail.documents';
          params = { id: user.companyId };
          break;
      }

      // $translate.use(user.locale);
      $translate.use(getStoredLanguage());

      yield put(actionsFactory.state(home, params));
      return authRes;
    } catch (err) {
      yield put(actionsFactory.authError(err.data || err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }
    return 'done';
  }

  function* loginFlow() {
    while (true) {
      const actionLogin = yield take([
        REQUEST_LOGIN, SKIP_LOGIN,
      ]);
      let task;
      if (actionLogin.type === REQUEST_LOGIN) {
        task = yield fork(authorize, actionLogin.username, actionLogin.password);
      }

      const action = yield take([REQUEST_LOGOUT, AUTH_ERROR]);
      if (action.type === REQUEST_LOGOUT && task && task.isRunning && task.isRunning()) {
        yield cancel(task);
      }

      // const state = $ngRedux.getState();
      // const locale = state.authentication.locale;
      $translate.use(getStoredLanguage());

      localStorage.removeItem('jwt_token');
      yield put(actionsFactory.state('not-authenticated.login'));
    }
  }

  function* updateLocale(action) {
    try {
      const localeRes = yield call(localeService.setLocale, action.locale);
      const locale = localeRes.data && localeRes.data.data;
      yield put(actionsFactory.updateLocaleSuccess(locale.locale));
      $translate.use(locale.locale);

      const state = $ngRedux.getState();
      const authenticated = state.authentication && state.authentication.isAuthenticated;
      const currentState = state.router.currentState.name;
      const pagesList = [
        'authenticated.company.detail.businessInfo',
        'authenticated.company.detail.techdoc',
        'authenticated.company.detail.questionnaire',
      ];
      if (authenticated && pagesList.indexOf(currentState) !== -1) {
        $state.reload();
      }
      return localeRes;
    } catch (err) {
      yield put(actionsFactory.updateLocaleError((err.data && err.data.error) || err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return 'done';
  }

  function* localeFlow() {
    yield takeLatest(UPDATE_LOCALE_START, updateLocale);
  }
}
